import type { DynamicSchema, DynamicUser, TableConfig, FieldSchema, CreationFieldConfig, DynamicFormData, FormValidationError } from "../types/dynamic"

/**
 * Servicio para gestionar schemas dinámicos
 */
export class SchemaService {
  /**
   * Procesa un schema y extrae la configuración de tabla
   */
  static processSchema(schema: DynamicSchema): TableConfig {
    const allVisibleFields: Array<{ name: string, position: number }> = []
    const editableFields: string[] = []
    let fixedField: string | undefined

    // Procesar cada campo del schema
    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      // Identificar campo fijo
      if (fieldConfig.fixed === true) {
        fixedField = fieldName
      }

      // Identificar campos visibles (por defecto true si no se especifica)
      if (fieldConfig.visible !== false) {
        allVisibleFields.push({
          name: fieldName,
          position: fieldConfig.position ?? 999 // Si no tiene position, ponerlo al final
        })
      }

      // Identificar campos editables
      if (fieldConfig.editable === true) {
        editableFields.push(fieldName)
      }
    })

    // Ordenar campos visibles por posición
    const sortedVisibleFields = allVisibleFields
      .sort((a, b) => a.position - b.position)
      .map(field => field.name)

    return {
      fixedField,
      visibleFields: sortedVisibleFields,
      editableFields
    }
  }

  /**
   * Get print fields from schema (fields that have print: true)
   */
  static getPrintFields(schema: DynamicSchema): CreationFieldConfig[] {
    return Object.entries(schema)
      .filter(([_, fieldSchema]) => fieldSchema.print === true)
      .map(([fieldName, fieldSchema]) => ({
        fieldName,
        fieldSchema,
        position: fieldSchema.position || 999,
        isMainField: ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints'].includes(fieldName)
      }))
      .sort((a, b) => a.position - b.position)
  }

  /**
   * Get the fixed field (field with fixed: true) for QR generation
   */
  static getFixedField(schema: DynamicSchema): string | null {
    const fixedField = Object.entries(schema).find(([_, fieldSchema]) => fieldSchema.fixed === true)
    return fixedField ? fixedField[0] : null
  }

  /**
   * Obtiene el valor de un campo de un usuario (campos principales o extra)
   */
  static getFieldValue(user: DynamicUser, fieldName: string): any {
    // Campos principales
    if (user.hasOwnProperty(fieldName)) {
      return user[fieldName]
    }
    
    // Campos en extra
    if (user.extra && user.extra.hasOwnProperty(fieldName)) {
      return user.extra[fieldName]
    }
    
    return null
  }

  /**
   * Formatea un valor según el tipo de campo
   */
  static formatFieldValue(value: any, fieldConfig: FieldSchema): string {
    if (value === null || value === undefined) {
      return "-"
    }

    switch (fieldConfig.type) {
      case 'boolean':
        return value ? 'Sí' : 'No'
      
      case 'number':
        return value.toString()
      
      case 'enum':
        return value.toString()
      
      case 'string':
      default:
        return value.toString()
    }
  }

  /**
   * Obtiene el campo que debe usarse como identificador principal
   * Prioridad: campo con fixed=true, luego 'id', luego el primer campo visible
   */
  static getDisplayIdField(schema: DynamicSchema, config: TableConfig): string {
    // Si hay un campo fijo, usarlo
    if (config.fixedField) {
      return config.fixedField
    }

    // Si existe 'id' en el schema, usarlo
    if (schema.id) {
      return 'id'
    }

    // Usar el primer campo visible como fallback
    return config.visibleFields[0] || 'id'
  }

  /**
   * Genera una cadena de búsqueda para un usuario
   * Incluye todos los campos visibles para facilitar la búsqueda
   */
  static generateSearchableText(user: DynamicUser, config: TableConfig): string {
    const searchableValues: string[] = []

    // Incluir ID siempre
    searchableValues.push(user.id.toString())

    // Incluir todos los campos visibles
    config.visibleFields.forEach(fieldName => {
      const value = this.getFieldValue(user, fieldName)
      if (value !== null && value !== undefined) {
        searchableValues.push(value.toString())
      }
    })

    return searchableValues.join(' ').toLowerCase()
  }

  /**
   * Normaliza texto para búsqueda (remueve acentos)
   */
  static normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ñ/g, "n")
  }

  /**
   * Filtra usuarios basado en un término de búsqueda
   */
  static filterUsers(
    users: DynamicUser[], 
    config: TableConfig, 
    searchTerm: string
  ): DynamicUser[] {
    if (!searchTerm.trim()) {
      return users
    }

    const normalizedSearch = this.normalizeText(searchTerm)

    return users.filter(user => {
      const searchableText = this.generateSearchableText(user, config)
      const normalizedSearchableText = this.normalizeText(searchableText)
      return normalizedSearchableText.includes(normalizedSearch)
    })
  }

  /**
   * Valida si un usuario cumple con los requisitos del schema
   */
  static validateUser(user: DynamicUser, schema: DynamicSchema): { valid: boolean; errors: string[] } {
    const errors: string[] = []

    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.required) {
        const value = this.getFieldValue(user, fieldName)
        if (value === null || value === undefined || value === '') {
          errors.push(`Campo requerido: ${fieldConfig.label}`)
        }
      }
    })

    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * Obtiene opciones para un campo tipo enum
   */
  static getFieldOptions(fieldConfig: FieldSchema): string[] {
    return fieldConfig.options || []
  }

  /**
   * Determina si un campo es de tipo select/dropdown
   */
  static isSelectField(fieldConfig: FieldSchema): boolean {
    return fieldConfig.type === 'enum' && Array.isArray(fieldConfig.options)
  }

  /**
   * Ordena usuarios por un campo específico
   */
  static sortUsers(
    users: DynamicUser[], 
    fieldName: string, 
    direction: 'asc' | 'desc' = 'asc'
  ): DynamicUser[] {
    return [...users].sort((a, b) => {
      const valueA = this.getFieldValue(a, fieldName)
      const valueB = this.getFieldValue(b, fieldName)

      // Manejar valores nulos
      if (valueA === null && valueB === null) return 0
      if (valueA === null) return direction === 'asc' ? 1 : -1
      if (valueB === null) return direction === 'asc' ? -1 : 1

      // Comparar valores
      if (valueA < valueB) return direction === 'asc' ? -1 : 1
      if (valueA > valueB) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  // =============================================================================
  // FUNCIONES PARA SISTEMA DE CREACIÓN DINÁMICO
  // =============================================================================

  /**
   * Procesa el schema para obtener campos de creación
   * Solo incluye campos con panelCreated: true
   */
  static getCreationFields(schema: DynamicSchema): CreationFieldConfig[] {
    const creationFields: CreationFieldConfig[] = []
    const mainFields = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']

    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.panelCreated === true) {
        creationFields.push({
          fieldName,
          fieldSchema: fieldConfig,
          position: fieldConfig.position ?? 999,
          isMainField: mainFields.includes(fieldName)
        })
      }
    })

    // Ordenar por posición
    return creationFields.sort((a, b) => a.position - b.position)
  }

  /**
   * Genera un valor único aleatorio para campos con defaultValue: "randomUnique"
   */
  static generateUniqueValue(
    fieldName: string, 
    existingUsers: DynamicUser[], 
    length: number = 10
  ): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let attempts = 0
    const maxAttempts = 100

    while (attempts < maxAttempts) {
      let randomValue = ''
      for (let i = 0; i < length; i++) {
        randomValue += characters.charAt(Math.floor(Math.random() * characters.length))
      }

      // Verificar que sea único
      const isUnique = !existingUsers.some(user => {
        const value = this.getFieldValue(user, fieldName)
        return value === randomValue
      })

      if (isUnique) {
        return randomValue
      }

      attempts++
    }

    // Si no se puede generar un valor único, usar timestamp
    return `${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).substr(2, 4).toUpperCase()}`
  }

  /**
   * Inicializa los valores por defecto del formulario
   */
  static initializeFormData(
    schema: DynamicSchema, 
    existingUsers: DynamicUser[]
  ): DynamicFormData {
    const formData: DynamicFormData = {}
    const creationFields = this.getCreationFields(schema)

    creationFields.forEach(({ fieldName, fieldSchema }) => {
      if (fieldSchema.defaultValue !== undefined) {
        if (fieldSchema.defaultValue === 'randomUnique') {
          formData[fieldName] = this.generateUniqueValue(fieldName, existingUsers)
        } else {
          formData[fieldName] = fieldSchema.defaultValue
        }
      } else {
        // Valores por defecto según tipo
        switch (fieldSchema.type) {
          case 'string':
          case 'enum':
            formData[fieldName] = ''
            break
          case 'number':
            formData[fieldName] = 0
            break
          case 'boolean':
            formData[fieldName] = false
            break
          default:
            formData[fieldName] = ''
        }
      }
    })

    return formData
  }

  /**
   * Valida los datos del formulario dinámico
   */
  static validateFormData(
    formData: DynamicFormData, 
    schema: DynamicSchema
  ): FormValidationError[] {
    const errors: FormValidationError[] = []
    const creationFields = this.getCreationFields(schema)

    creationFields.forEach(({ fieldName, fieldSchema }) => {
      const value = formData[fieldName]

      // Validación de campos requeridos
      if (fieldSchema.required) {
        if (value === null || value === undefined || value === '' || 
            (typeof value === 'string' && value.trim() === '')) {
          errors.push({
            field: fieldName,
            message: `${fieldSchema.label} es requerido`
          })
          return
        }
      }

      // Validaciones específicas por tipo
      if (value !== null && value !== undefined && value !== '') {
        switch (fieldSchema.type) {
          case 'string':
            if (typeof value !== 'string') {
              errors.push({
                field: fieldName,
                message: `${fieldSchema.label} debe ser texto`
              })
            } else if (value.length > 255) {
              errors.push({
                field: fieldName,
                message: `${fieldSchema.label} es muy largo (máx. 255 caracteres)`
              })
            }
            break

          case 'number':
            if (typeof value !== 'number' && isNaN(Number(value))) {
              errors.push({
                field: fieldName,
                message: `${fieldSchema.label} debe ser un número`
              })
            }
            break

          case 'boolean':
            if (typeof value !== 'boolean') {
              errors.push({
                field: fieldName,
                message: `${fieldSchema.label} debe ser verdadero o falso`
              })
            }
            break

          case 'enum':
            break
        }

        // Validación específica para email
        if (fieldName === 'email' && typeof value === 'string') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(value)) {
            errors.push({
              field: fieldName,
              message: 'Formato de correo inválido'
            })
          }
        }

        // Validación específica para teléfono
        if (fieldName === 'telefono' && typeof value === 'string') {
          const phoneDigits = value.replace(/[^\d]/g, '')
          if (phoneDigits.length < 8) {
            errors.push({
              field: fieldName,
              message: 'Teléfono muy corto (mín. 8 dígitos)'
            })
          }
        }
      }
    })

    return errors
  }

  /**
   * Convierte los datos del formulario al formato de la API
   */
  static formatForApi(
    formData: DynamicFormData
  ): { mainFields: any, extraFields: any } {
    const mainFields: any = {}
    const extraFields: any = {}
    const mainFieldNames = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']

    Object.entries(formData).forEach(([fieldName, value]) => {
      if (mainFieldNames.includes(fieldName)) {
        mainFields[fieldName] = value
      } else {
        extraFields[fieldName] = value
      }
    })

    return { mainFields, extraFields }
  }

  /**
   * Obtiene valores únicos existentes para un campo específico
   */
  static getExistingValues(
    fieldName: string, 
    users: DynamicUser[]
  ): Set<any> {
    const values = new Set()
    users.forEach(user => {
      const value = this.getFieldValue(user, fieldName)
      if (value !== null && value !== undefined) {
        values.add(value)
      }
    })
    return values
  }

  // =============================================================================
  // FUNCIONES PARA MERGE OFFLINE-FIRST
  // =============================================================================

  /**
   * Merge inteligente que prioriza datos locales (offline-first)
   * Los usuarios locales siempre tienen prioridad sobre los del servidor
   */
  static mergeUsersOfflineFirst(
    localUsers: DynamicUser[], 
    serverUsers: DynamicUser[]
  ): DynamicUser[] {
    console.log(`🔄 Mergeando usuarios: ${localUsers.length} locales + ${serverUsers.length} servidor`)

    // Crear mapa de usuarios locales por ID para búsqueda rápida
    const localUsersMap = new Map<number, DynamicUser>()
    localUsers.forEach(user => localUsersMap.set(user.id, user))

    // Crear mapa de usuarios del servidor por ID
    const serverUsersMap = new Map<number, DynamicUser>()
    serverUsers.forEach(user => serverUsersMap.set(user.id, user))

    // Resultado final: empezar con todos los usuarios locales
    const mergedUsers: DynamicUser[] = [...localUsers]

    // Agregar usuarios del servidor que NO están en local
    serverUsers.forEach(serverUser => {
      if (!localUsersMap.has(serverUser.id)) {
        mergedUsers.push(serverUser)
        console.log(`📥 Agregando usuario del servidor no local: ID ${serverUser.id}`)
      }
    })

    // Ordenar por ID descendente
    const sortedUsers = mergedUsers.sort((a, b) => b.id - a.id)
    
    console.log(`✅ Merge completado: ${sortedUsers.length} usuarios finales (prioridad local)`)
    return sortedUsers
  }

  /**
   * Determina si los datos locales y del servidor son diferentes
   * Solo considera cambios significativos para evitar actualizaciones innecesarias
   */
  static hasSignificantDataChanges(
    localUsers: DynamicUser[], 
    serverUsers: DynamicUser[]
  ): boolean {
    // Si las cantidades son diferentes, hay cambios
    if (localUsers.length !== serverUsers.length) {
      return true
    }

    // Crear mapas por ID para comparación eficiente
    const localMap = new Map(localUsers.map(user => [user.id, user]))
    const serverMap = new Map(serverUsers.map(user => [user.id, user]))

    // Verificar si hay usuarios diferentes por ID
    for (const [id, serverUser] of serverMap) {
      const localUser = localMap.get(id)
      
      // Si el usuario del servidor no existe en local, hay cambios
      if (!localUser) {
        return true
      }

      // Comparar datos principales (excluyendo timestamps que pueden cambiar)
      const serverDataWithoutTimestamps = { ...serverUser }
      const localDataWithoutTimestamps = { ...localUser }
      
      // Remover campos que pueden cambiar sin ser significativos
      delete serverDataWithoutTimestamps.updated_at
      delete serverDataWithoutTimestamps.created_at
      delete localDataWithoutTimestamps.updated_at
      delete localDataWithoutTimestamps.created_at

      if (JSON.stringify(serverDataWithoutTimestamps) !== JSON.stringify(localDataWithoutTimestamps)) {
        return true
      }
    }

    return false
  }

  /**
   * Obtiene el campo configurado como único (fixed: true)
   */
  static getUniqueField(schema: DynamicSchema): string | null {
    for (const [fieldName, fieldConfig] of Object.entries(schema)) {
      if (fieldConfig.fixed === true) {
        return fieldName
      }
    }
    
    // Fallback: buscar campos que podrían ser únicos
    if (schema.codigo_qr) return 'codigo_qr'
    if (schema.email) return 'email'
    
    return null
  }

  /**
   * Obtiene el valor del campo único de un usuario
   */
  static getUniqueFieldValue(user: DynamicUser, schema: DynamicSchema): string | null {
    const uniqueField = this.getUniqueField(schema)
    if (!uniqueField) return null
    
    const value = this.getFieldValue(user, uniqueField)
    return value ? String(value) : null
  }

  /**
   * Merge inteligente usando campo único (fixed) como identificador
   * Previene duplicados al identificar usuarios por campo único en lugar de solo ID
   */
  static mergeUsersByUniqueField(
    localUsers: DynamicUser[], 
    serverUsers: DynamicUser[],
    schema: DynamicSchema
  ): DynamicUser[] {
    const uniqueField = this.getUniqueField(schema)
    
    if (!uniqueField) {
      console.warn("⚠️ No se encontró campo único, usando merge por ID")
      return this.mergeUsersOfflineFirst(localUsers, serverUsers)
    }

    console.log(`🔑 Usando campo único para merge: ${uniqueField}`)
    console.log(`🔄 Mergeando ${localUsers.length} usuarios locales + ${serverUsers.length} usuarios del servidor`)

    // Crear mapa de usuarios locales por valor único
    const localByUniqueValue = new Map<string, DynamicUser>()
    localUsers.forEach(user => {
      const uniqueValue = this.getUniqueFieldValue(user, schema)
      if (uniqueValue) {
        localByUniqueValue.set(uniqueValue, user)
      }
    })

    // Crear mapa de usuarios del servidor por valor único
    const serverByUniqueValue = new Map<string, DynamicUser>()
    serverUsers.forEach(user => {
      const uniqueValue = this.getUniqueFieldValue(user, schema)
      if (uniqueValue) {
        serverByUniqueValue.set(uniqueValue, user)
      }
    })

    const mergedUsers: DynamicUser[] = []
    const processedUniqueValues = new Set<string>()

    // Procesar usuarios del servidor (tienen IDs reales)
    serverUsers.forEach(serverUser => {
      const serverUniqueValue = this.getUniqueFieldValue(serverUser, schema)
      
      if (serverUniqueValue && !processedUniqueValues.has(serverUniqueValue)) {
        const localUser = localByUniqueValue.get(serverUniqueValue)
        
        if (localUser) {
          // Usuario existe tanto local como en servidor - usar datos del servidor con ID real
          // pero preservar algunos campos locales si es necesario
          const mergedUser = {
            ...serverUser, // Base: datos del servidor (con ID real)
            // Preservar algunos campos locales críticos si es necesario
          }
          
          mergedUsers.push(mergedUser)
          console.log(`🔄 Usuario temporal reemplazado: ${serverUniqueValue} (ID temporal: ${localUser.id} → ID real: ${serverUser.id})`)
        } else {
          // Usuario nuevo del servidor
          mergedUsers.push(serverUser)
          console.log(`📥 Usuario nuevo del servidor: ${serverUniqueValue} (ID: ${serverUser.id})`)
        }
        
        processedUniqueValues.add(serverUniqueValue)
      }
    })

    // Agregar usuarios que están solo en local (pendientes de sincronización)
    localUsers.forEach(localUser => {
      const localUniqueValue = this.getUniqueFieldValue(localUser, schema)
      
      if (localUniqueValue && !processedUniqueValues.has(localUniqueValue)) {
        mergedUsers.push(localUser)
        console.log(`💾 Usuario solo local preservado: ${localUniqueValue} (ID temporal: ${localUser.id})`)
        processedUniqueValues.add(localUniqueValue)
      }
    })

    // Ordenar por ID descendente
    const sortedUsers = mergedUsers.sort((a, b) => b.id - a.id)
    
    console.log(`✅ Merge por campo único completado: ${sortedUsers.length} usuarios finales`)
    console.log(`🔑 Campo único usado: ${uniqueField}`)
    
    return sortedUsers
  }

  /**
   * Identifica usuarios que están solo en local (pendientes de sincronización)
   */
  static getLocalOnlyUsers(
    localUsers: DynamicUser[], 
    serverUsers: DynamicUser[]
  ): DynamicUser[] {
    const serverIds = new Set(serverUsers.map(user => user.id))
    return localUsers.filter(user => !serverIds.has(user.id))
  }

  /**
   * Identifica usuarios temporales que deben ser limpiados
   */
  static getTemporaryUsersToClean(
    localUsers: DynamicUser[], 
    serverUsers: DynamicUser[],
    schema: DynamicSchema
  ): DynamicUser[] {
    const uniqueField = this.getUniqueField(schema)
    if (!uniqueField) return []

    const serverUniqueValues = new Set<string>()
    serverUsers.forEach(user => {
      const uniqueValue = this.getUniqueFieldValue(user, schema)
      if (uniqueValue) serverUniqueValues.add(uniqueValue)
    })

    // Identificar usuarios locales que ya tienen versión en servidor
    return localUsers.filter(localUser => {
      const localUniqueValue = this.getUniqueFieldValue(localUser, schema)
      if (!localUniqueValue) return false
      
      // Si el usuario local existe en servidor Y tiene ID temporal, debe ser limpiado
      const hasServerVersion = serverUniqueValues.has(localUniqueValue)
      const hasTemporaryId = localUser.id > 1000000000000 // IDs temporales son timestamps
      
      return hasServerVersion && hasTemporaryId
    })
  }
}
